import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Edit2, Trash2, Tag, Search } from 'lucide-react';
import { toast } from 'sonner';
import ItemTypesPanel from '../components/ItemTypesPanel';

const DEFAULT_FORM = { name: '', description: '', color: '#f97316', display_order: 0, active_status: true };

const ItemCategoriesPage = () => {
  const { api, user } = useAuth();
  const isManager = user && (user.role === 'Admin' || user.role === 'InventoryManager');
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catsRes, itemsRes] = await Promise.all([
        api().get('/item-categories'),
        api().get('/items'),
      ]);
      setCategories(catsRes.data);
      // Count items per category
      const countMap = {};
      (itemsRes.data || []).forEach(it => {
        const k = (it.item_category || '').trim();
        if (k) countMap[k] = (countMap[k] || 0) + 1;
      });
      setCounts(countMap);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...DEFAULT_FORM, display_order: categories.length });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setFormData({
      name: cat.name || '',
      description: cat.description || '',
      color: cat.color || '#f97316',
      display_order: cat.display_order ?? 0,
      active_status: cat.active_status !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api().put(`/item-categories/${editing.id}`, formData);
        toast.success('Category updated');
      } else {
        await api().post('/item-categories', formData);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    const usage = counts[cat.name] || 0;
    if (usage > 0) {
      toast.error(`'${cat.name}' is used by ${usage} item(s). Reassign or deactivate instead.`);
      return;
    }
    if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await api().delete(`/item-categories/${cat.id}`);
      toast.success('Category deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = categories.filter(c =>
    !searchTerm ||
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="item-categories-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Tag className="w-8 h-8 text-orange-500" />
            Item Categories
          </h1>
          <p className="text-slate-500 mt-1">Manage the categories that appear in the Item Master.</p>
        </div>
        {isManager && (
          <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="new-category-btn">
            <Plus className="w-4 h-4 mr-2" /> New Category
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="border-b">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="category-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No categories found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead className="text-center">Order</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  {isManager && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(cat => (
                  <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full border border-slate-200"
                          style={{ backgroundColor: cat.color || '#cbd5e1' }}
                        />
                        <span className="font-medium">{cat.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">{cat.description || '—'}</TableCell>
                    <TableCell className="text-center">{counts[cat.name] || 0}</TableCell>
                    <TableCell className="text-center text-slate-500">{cat.display_order}</TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${cat.active_status ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {cat.active_status ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    {isManager && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(cat)} data-testid={`edit-category-${cat.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(cat)} className="text-red-600" data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* iter76 — Customizable Item Types (Product Types) */}
      <ItemTypesPanel api={api} canEdit={isManager} />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update this category. Renaming will cascade to all items using it.' : 'Add a new item category.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="cat-name">Name *</Label>
              <Input
                id="cat-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Electronics"
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="category-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cat-color">Color</Label>
                <Input
                  id="cat-color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10"
                  data-testid="category-color-input"
                />
              </div>
              <div>
                <Label htmlFor="cat-order">Display Order</Label>
                <Input
                  id="cat-order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value || 0, 10) })}
                  data-testid="category-order-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Label htmlFor="cat-active" className="cursor-pointer">Active</Label>
              <Switch
                id="cat-active"
                checked={formData.active_status}
                onCheckedChange={(v) => setFormData({ ...formData, active_status: v })}
                data-testid="category-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving} data-testid="category-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="category-save-btn">
              {saving ? 'Saving...' : (editing ? 'Save' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemCategoriesPage;
